import { createFileRoute, useNavigate } from '@tanstack/react-router';

import type { TransactionFilters } from '@/features/admin/api/transactions';
import { TransactionsPage } from '@/features/admin/components/transactions-page';
import { type PaginationSearch, validatePaginationSearch } from '@/lib/pagination-search';
import { manyStrings } from '@/lib/query-params';

/**
 * Search params the transaction list accepts: the filter set, paging and
 * ordering.
 *
 * All of it lives in the URL, as it does on the user list, so a filtered view
 * survives a drill-down and back rather than resetting to everything. The
 * history spam that argued for component state is handled instead by the two
 * things that actually address it: the free-text box debounces, and every write
 * replaces rather than pushes.
 */
export interface TransactionsSearch extends TransactionFilters, PaginationSearch {}

/**
 * Narrows the search params, dropping anything the API would reject.
 *
 * Anything can be typed into an address bar, and an unrecognised value must fall
 * back rather than become a failed request.
 */
function validateSearch(search: Record<string, unknown>): TransactionsSearch {
  const result: TransactionsSearch = validatePaginationSearch(search);

  for (const key of ['user', 'companyId'] as const) {
    const value = search[key];
    if (typeof value === 'string' && value) {
      result[key] = value;
    }
  }

  // Several types may be named at once, comma-separated. Not checked against a
  // fixed set: `type` is a TransactionType on one side of the union and a
  // PaymentStatus on the other, and the API is the boundary that knows both.
  const type = manyStrings(search.type);
  if (type) result.type = type;

  if (search.source === 'balance' || search.source === 'payment') {
    result.source = search.source;
  }
  // Date bounds are kept only in the `YYYY-MM-DD` form the picker produces and
  // the API expects. Anything else is dropped rather than forwarded, so a
  // hand-edited URL degrades to an unfiltered list instead of a 400.
  for (const key of ['from', 'to'] as const) {
    const value = search[key];
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      result[key] = value;
    }
  }

  if (
    search.sortBy === 'occurredAt' ||
    search.sortBy === 'amountPence' ||
    search.sortBy === 'type'
  ) {
    result.sortBy = search.sortBy;
  }
  if (search.sortOrder === 'asc' || search.sortOrder === 'desc') {
    result.sortOrder = search.sortOrder;
  }

  return result;
}

export const Route = createFileRoute('/admin/transactions')({
  validateSearch,
  component: TransactionsRoute,
});

function TransactionsRoute() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <TransactionsPage
      filters={filters}
      onFiltersChange={(next) => navigate({ search: next, replace: true })}
    />
  );
}
